import { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { BusinessListingsApi } from '../services/FirebaseApiService';

const functions = getFunctions(undefined, 'us-central1');

/**
 * useFeaturedListings
 *
 * Returns a priority-ordered pool of showcase-eligible listings for the homepage
 * hero carousel and the R5 featured grid.
 *
 * Pool composition:
 *   1. All listings with active promotions (premium → featured → spotlight → boost)
 *   2. Top active listings by viewCount used to fill up to `minPool` slots
 *
 * Falls back to a direct Firestore query if the Cloud Function is unavailable.
 */
export function useFeaturedListings(minPool = 5) {
  const [pool, setPool]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        // Primary: Cloud Function returns promotion-sorted listings
        const getActive = httpsCallable(functions, 'getActivePromotions');
        const { data } = await getActive({ limit: 20 });
        const promoted = (data.listings || []).filter((l) => l.status === 'active');

        if (cancelled) return;

        if (promoted.length >= minPool) {
          setPool(promoted);
        } else {
          // Fill remaining slots with top-viewCount active listings
          const all = await BusinessListingsApi.getAll();
          if (cancelled) return;
          const promotedIds = new Set(promoted.map((l) => l.id));
          const fill = all
            .filter((l) => l.status === 'active' && !promotedIds.has(l.id))
            .sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0))
            .slice(0, minPool - promoted.length);
          setPool([...promoted, ...fill]);
        }
      } catch {
        // Fallback: direct Firestore read — no promotion ordering
        try {
          const all = await BusinessListingsApi.getAll();
          if (cancelled) return;
          setPool(all.filter((l) => l.status === 'active').slice(0, minPool));
        } catch (fallbackErr) {
          if (cancelled) return;
          setError(fallbackErr);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [minPool]);

  return { pool, loading, error };
}
